import { useResponsiveValues } from '@group-link-one/gl-components';
import {
  SPBox,
  SPButton,
  ColorVariables,
  BaseProperties,
  SPTypography,
} from '@group-link-one/gl-ui/sonarprint';
import { FallbackArt } from './components/FallBackArt';
import { FallbackBG } from './components/FallbackBG';
import { FallBackStyled } from './FallBackTemplate.style';
import type { IFallBackProps } from './Types/FallBackTemplate.types';
import { useFallBackTemplate } from './useFallBack.template';

const FallbackTemplate = ({
  onClickButton,
  logoOptions,
  title,
  subtitle,
  description,
  hasButton = true,
  customHeight,
}: IFallBackProps) => {
  const { customLocale, isDarkMode } = useFallBackTemplate();
  const { Large } = useResponsiveValues();

  return (
    <SPBox
      className={FallBackStyled}
      padding={Large ? '80px' : BaseProperties.SPACING.XL}
      paddingBottom='0px'
      height={customHeight ? customHeight : '100vh'}
      gap={BaseProperties.SPACING.L}
      backgroundColor={ColorVariables.BASE.BASE_COLOR_QUINARY_BACKGROUND}
      position='relative'
      css={`
        .not-found-logo {
          width: ${Large ? '250px' : '165px'};
          height: ${Large ? '60px' : '40px'};
          object-fit: contain;
        }
      `}
    >
      <SPBox height='100%' gap='80px' flexDirection='column'>
        <SPBox width={Large ? '250px' : '165px'} height={Large ? '60px' : '40px'}>
          <img
            className='not-found-logo'
            src={isDarkMode ? logoOptions.src.logoDark : logoOptions.src.logoLight}
            alt={logoOptions.alt}
          />
        </SPBox>

        <SPBox flexDirection='column' gap={BaseProperties.SPACING.M} maxWidth='750px'>
          <SPBox flexDirection='column' width='100%'>
            <SPTypography
              color={ColorVariables.ACCENT.ACCENT_COLOR_PRIMARY_TEXT}
              fontSize={BaseProperties.FONT_SIZE.HEADLINE_6}
              fontWeight={600}
            >
              {title}
            </SPTypography>

            <SPTypography
              as='h3'
              color={ColorVariables.BASE.BASE_COLOR_PRIMARY_TEXT}
              fontSize={
                Large ? BaseProperties.FONT_SIZE.HEADLINE_3 : BaseProperties.FONT_SIZE.HEADLINE_5
              }
              fontWeight={600}
            >
              {subtitle}
            </SPTypography>
          </SPBox>

          <SPBox flexDirection='column' gap={BaseProperties.SPACING.M}>
            <SPTypography
              as='p'
              color={ColorVariables.BASE.BASE_COLOR_SECONDARY_TEXT}
              fontSize={BaseProperties.FONT_SIZE.SUBTITLE_2}
            >
              {description}
            </SPTypography>
            {hasButton && onClickButton && (
              <SPButton
                maxWidth='300px'
                variant='primary'
                text={customLocale('notFound.button')}
                align='center'
                onClick={onClickButton}
              />
            )}
          </SPBox>
        </SPBox>
      </SPBox>
      <FallbackBG
        startStopColor={ColorVariables.ACCENT.ACCENT_COLOR_PRIMARY_BACKGROUND}
        middleStopColor={ColorVariables.ACCENT.ACCENT_COLOR_PRIMARY_BACKGROUND_50}
        endStopColor={ColorVariables.ACCENT.ACCENT_COLOR_PRIMARY_BACKGROUND_10}
      />

      {Large && <FallbackArt />}
    </SPBox>
  );
};

export { FallbackTemplate };
